import { formatDateText } from '@/lib/dateDisplay';
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '@/hooks/useNotifications';
import { cn, formatDateTime } from '@/lib/utils';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { useRole } from '@/hooks/useRole';
import { notificationTargetPath } from '@/lib/notificationRoutes';
import type { Notification } from '@/types/database.types';
import { WorklynxIcon } from '@/components/icons/WorklynxIcon';

export function NotificationBell() {
  const { notifications, unreadCount, isLoading, markAsRead, markAllAsRead } =
    useNotifications();
  const { role } = useRole();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    }

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      markAsRead.mutate(notification.id);
    }

    setOpen(false);

    if (role) {
      navigate(notificationTargetPath(notification, role));
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-950 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-white"
        aria-label="Notifications"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <WorklynxIcon name="notifications" size={20} />
        {unreadCount > 0 && (
          <span className="absolute right-1 top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <>
          <button
            type="button"
            aria-label="Close notifications"
            className="fixed inset-0 z-[90] bg-slate-950/20 backdrop-blur-[1px] sm:hidden"
            onClick={() => setOpen(false)}
          />
          <div className="fixed left-3 right-3 top-[5.25rem] z-[100] max-h-[70dvh] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-950/20 dark:border-slate-700 dark:bg-slate-900 sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-3 sm:w-[24rem] sm:max-h-none sm:shadow-xl sm:shadow-slate-900/10">
            <div className="flex items-center justify-between gap-3 border-b border-slate-100 p-4 dark:border-slate-800">
              <h3 className="text-sm font-semibold text-foreground">Notifications</h3>
              {unreadCount > 0 && (
                <button
                  onClick={() => markAllAsRead.mutate()}
                  disabled={markAllAsRead.isPending}
                  className="inline-flex items-center gap-1 text-xs text-brand hover:underline disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <WorklynxIcon name="success" size={14} />
                  Mark all read
                </button>
              )}
            </div>

            <div className="scrollbar-thin max-h-[calc(70dvh-3.5rem)] overflow-y-auto sm:max-h-80">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner size="sm" />
                </div>
              ) : notifications.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted">No notifications</p>
              ) : (
                <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                  {notifications.slice(0, 20).map((n) => (
                    <li
                      key={n.id}
                      className={cn(!n.is_read && 'bg-brand-soft/40')}
                    >
                      <button
                        type="button"
                        className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50 dark:hover:bg-slate-800"
                        onClick={() => handleNotificationClick(n)}
                      >
                        <span
                          className={cn(
                            'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                            n.is_read ? 'bg-transparent' : 'bg-brand',
                          )}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block break-words text-sm font-medium text-foreground">
                            {n.title}
                          </span>
                          {n.body && (
                            <span className="mt-0.5 block break-words text-xs leading-5 text-muted">
                              {formatDateText(n.body ?? '')}
                            </span>
                          )}
                          <span className="mt-1 block text-[10px] text-muted">
                            {formatDateTime(n.created_at)}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
